import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import Loader from '__components/Kizen/Loader';
import { useToast } from '__components/ToastProvider';
import { getOriginalError } from '__services/AxiosService';
import { getErrorMessage } from '__hooks/useErrors';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { mcpServerQuery, mcpServerStepOptionsQuery } from 'query/mcp';
import MCPServerService from 'services/MCPServerService';
import type { MCPServerConfigSharingSettings } from '@kizen/api/mcp';
import { SettingsContentCard } from '__components/Card/Card';
import PermissionSettings from '__components/Sharing/PermissionSettings';
import { getSharingLabels } from 'pages/MCPServerSettings/constants';
import { buildSharingSettingsForAPI } from 'utility/sharing';
import { useSetSaveAction } from '../ToolbarContext';

const StyledSettingsContentCard = styled(SettingsContentCard)`
  padding-top: 20px;
  margin-top: 20px;
`;

export const MCPServerTeamSharingSettings = () => {
  const { t } = useTranslation();
  const { mcpServerId } = useParams<{ mcpServerId: string }>();
  const [showToast, toastVariant] = useToast();
  const queryClient = useQueryClient();
  const setSaveAction = useSetSaveAction();

  const queryOptions = useMemo(
    () =>
      mcpServerQuery(mcpServerId, {
        placeholderData: keepPreviousData,
        staleTime: 0,
        enabled: !!mcpServerId,
      }),
    [mcpServerId]
  );

  const { data: server, isLoading } = useQuery(queryOptions);

  const [permissions, setPermissions] = useState<any>(null);
  const [isDirty, setIsDirty] = useState(false);

  const handlePermissionsChange = useCallback((state: any, dirty: boolean) => {
    setPermissions(state);
    if (dirty) setIsDirty(true);
  }, []);

  const { mutate: saveSharingSettings, isPending: isSaving } = useMutation({
    mutationFn: () =>
      MCPServerService.updateMCPServer(mcpServerId, {
        sharing_settings: (
          buildSharingSettingsForAPI(permissions) as {
            sharing_settings?: MCPServerConfigSharingSettings;
          }
        )?.sharing_settings,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryOptions.queryKey });
      queryClient.invalidateQueries({
        queryKey: mcpServerStepOptionsQuery().queryKey,
      });
      setIsDirty(false);
      showToast({
        message: t('Sharing settings saved successfully'),
        variant: toastVariant.SUCCESS,
      });
    },
    onError: (error) => {
      showToast({
        message:
          getErrorMessage(getOriginalError(error)) ||
          t('Failed to save sharing settings'),
        variant: toastVariant.FAILURE,
      });
    },
  });

  const handleSave = useCallback(() => {
    saveSharingSettings();
  }, [saveSharingSettings]);

  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;

  useEffect(() => {
    setSaveAction({
      onSave: () => handleSaveRef.current(),
      disabled: !isDirty || isSaving,
    });
    return () => setSaveAction(null);
  }, [isDirty, isSaving, setSaveAction]);

  return (
    <Loader loading={isLoading}>
      <StyledSettingsContentCard>
        <PermissionSettings
          onChange={handlePermissionsChange}
          existing={server?.sharing_settings ?? {}}
          loading={isLoading}
          disableFocus
          removeTopPadding
          includeServiceAccounts
          labels={getSharingLabels(t)}
        />
      </StyledSettingsContentCard>
    </Loader>
  );
};
